/*
  AutocompleteString.jsx
*/

// Npm.
import React, { useState, useEffect, useMemo } from 'react';

// Components.
import TextField from './TextField';
import List from './List';

// Makes.
import { makeStyles, cx } from '../makes/makeStyles';

// Styles.
const useStyles = makeStyles(theme => ({
  root: {
    '& > .options': {
      position: 'fixed',
      zIndex: 10,
      minWidth: 200,
      maxHeight: 256,
      backgroundColor: theme.color_paper,
      boxShadow: theme.shadow(3),
      borderRadius: 4,
      overflowY: 'auto',
    }
  },
}));

/**
 * A wrapper around text field that provides a dropdown of autocomplete options.
 */
export default function AutocompleteString({
  className,
  value,
  options,
  onChange,
  onInputChange,
  onKeyDown,
  onFocus,
  onBlur,
  disabled = false,
  disableCloseOnSelect = false,
  ...rest
}) {

  value = value || '';
  options = options || [];

  // State.

  const [localValue, setLocalValue] = useState(value);
  const [focusStyle, setFocusStyle] = useState(null);

  // Filter options by local value.

  if (localValue && options.length > 0) {
    const lc = localValue.toLowerCase();
    options = options.filter(opt => opt.toLowerCase().includes(lc));
  }

  // Effects.

  const [rerender, setRerender] = useState(0);

  useEffect(() => {
    setLocalValue(value);
  }, [value, rerender]);

  useEffect(() => {

    function handleResize() {
      setFocusStyle(null);
    }

    // TODO: Add resize listener?

    window.addEventListener('resize', handleResize, false);
    window.addEventListener('scroll', handleResize, false);

    return () => {
      window.removeEventListener('resize', handleResize, false);
      window.removeEventListener('scroll', handleResize, false);
    };

  }, []);

  // Handlers.

  function updateValue(next, type) {
    if (onChange && next !== value) {
      onChange(next, type);
      setRerender(n => n + 1);  // Re-render to refresh localValue.
    }
  }

  function updateBox(el) {
    const box = el.getBoundingClientRect();
    setFocusStyle({
      top: box.bottom,
      left: box.left,
      width: box.width,
    });
  }

  function handleChange(event) {
    setLocalValue(event.target.value);
    updateBox(event.target);
    if (onInputChange) {
      onInputChange(event);
    }
  }

  function handleFocus(event) {
    updateBox(event.currentTarget);
    if (onFocus) {
      onFocus(event);
    }
  }

  function handleBlur(event) {
    setFocusStyle(null);
    updateValue(localValue, 'blur');
    if (onBlur) {
      onBlur(event);
    }
  }

  function handleKeyDown(event) {
    if (event.key === 'Enter') {
      updateValue(localValue, 'enter');
    }
    if (onKeyDown) {
      onKeyDown(event);
    }
  }

  function handleMouseDown(event) {
    if (focusStyle) {
      event.preventDefault();  // Stops blur on input.
    }
  }

  function handleClickOption(event, opt) {
    if (!disableCloseOnSelect) {
      setFocusStyle(null);
    }
    updateValue(opt, 'select');
  }

  // Classes.

  const classes = useStyles();
  const cls = useMemo(() => cx('AutocompleteString', classes.root, className), [classes.root, className]);

  // TODO: Allow keyboard focus and arrow for the autocomplete options.

  return (
    <div className={cls} onMouseDown={handleMouseDown}>
      <TextField
        value={localValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={disabled}
        {...rest}
      />
      {
        !disabled && focusStyle != null && options.length > 0 &&
        <div
          className="options"
          style={focusStyle}
        >
          <List
            options={options}
            onClickOption={handleClickOption}
          />
        </div>
      }
    </div>
  );
}
